// bundles/livcg/panels/src/match-day/deck/DeckGrid.tsx
// 12×8 tile field. Live mode: widgets are interactive. Edit mode:
// tile dnd (the GridPanel pointer pattern: pointer capture is released and
// movement is tracked in the grid container's onPointerMove; if the finger
// leaves the grid and is released, a `window`-level pointerup
// listener cancels the drag immediately — the ghost-drag state does not linger
// until the next pointerdown), bottom-right corner resize, an invalid
// spot is red, on release it returns to the previous spot. Only one drag at
// a time (bound to a pointerId) — another finger cannot steal it.
import { DECK_GRID_COLS, DECK_GRID_ROWS, type DeckTile, type ProjectState } from "@livcg/schema";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../ui/index.js";
import { placementValid } from "./layout.js";
import { widgetDef, type DeckServices } from "./registry.js";

type Drag = {
  tileId: string;
  kind: "move" | "resize";
  /** The pointer that started this drag — so another finger (multi-touch) cannot
   *  steal it and move/up/cancel are accepted only from this pointer. */
  pointerId: number;
  /** move: offset between the finger and the tile's corner (in grid units). */
  offX: number;
  offY: number;
  /** The current candidate (in grid coordinates) and whether it is valid. */
  cand: DeckTile;
  valid: boolean;
};

type Props = {
  slug: string;
  state: ProjectState;
  tiles: DeckTile[];
  editing: boolean;
  services: DeckServices;
  onChange: (tiles: DeckTile[]) => void;
  onRemove: (tileId: string) => void;
};

export function DeckGrid({ slug, state, tiles, editing, services, onChange, onRemove }: Props) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  /** Pointer → grid cell (not fractional, floor). */
  const toCell = (e: React.PointerEvent) => {
    const el = gridRef.current;
    if (!el) return { gx: 0, gy: 0 };
    const r = el.getBoundingClientRect();
    const cell = r.width / DECK_GRID_COLS;
    return {
      gx: Math.floor((e.clientX - r.left) / cell),
      gy: Math.floor((e.clientY - r.top) / cell),
    };
  };

  const startMove = (e: React.PointerEvent, t: DeckTile) => {
    if (!editing) return;
    // If another finger has already started a drag, the new one must not
    // steal it (I2: two simultaneous drags colliding under multi-touch).
    if (drag) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    const { gx, gy } = toCell(e);
    setDrag({ tileId: t.id, kind: "move", pointerId: e.pointerId, offX: gx - t.x, offY: gy - t.y, cand: t, valid: true });
  };

  const startResize = (e: React.PointerEvent, t: DeckTile) => {
    if (!editing) return;
    if (drag) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setDrag({ tileId: t.id, kind: "resize", pointerId: e.pointerId, offX: 0, offY: 0, cand: t, valid: true });
  };

  const onMove = (e: React.PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const t = tiles.find((x) => x.id === drag.tileId);
    if (!t) return;
    const { gx, gy } = toCell(e);
    const def = widgetDef(t.widget);
    const minW = def?.minW ?? 1;
    const minH = def?.minH ?? 1;
    const cand: DeckTile =
      drag.kind === "move"
        ? { ...t, x: gx - drag.offX, y: gy - drag.offY }
        : { ...t, w: Math.max(minW, gx - t.x + 1), h: Math.max(minH, gy - t.y + 1) };
    setDrag({ ...drag, cand, valid: placementValid(tiles, cand) });
  };

  const onUp = (e: React.PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    if (drag.valid) onChange(tiles.map((t) => (t.id === drag.tileId ? drag.cand : t)));
    setDrag(null);
  };

  const onCancel = (e: React.PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    setDrag(null);
  };

  // M1: if the finger is released OUTSIDE the grid (the onPointerUp inside the
  // grid is not reached), the ghost-drag state must not stay "alive" until the
  // next pointerdown — we listen at the window level and cancel the drag when the
  // pointerId matches (revert, not commit — only an up inside the grid commits).
  // The dependency is only pointerId (drag start/end), to avoid re-subscribing on
  // every pointermove (on cand/valid updates).
  const dragPointerId = drag?.pointerId ?? null;
  useEffect(() => {
    if (dragPointerId === null) return;
    const handler = (e: PointerEvent) => {
      if (e.pointerId === dragPointerId) setDrag(null);
    };
    window.addEventListener("pointerup", handler);
    return () => window.removeEventListener("pointerup", handler);
  }, [dragPointerId]);

  return (
    <div
      ref={gridRef}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      className="relative w-full touch-none select-none"
      style={{ aspectRatio: `${DECK_GRID_COLS} / ${DECK_GRID_ROWS}` }}
    >
      {/* Grid lines while editing */}
      {editing && (
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(to right, var(--p-border) 1px, transparent 1px), linear-gradient(to bottom, var(--p-border) 1px, transparent 1px)",
            backgroundSize: `${100 / DECK_GRID_COLS}% ${100 / DECK_GRID_ROWS}%`,
          }}
        />
      )}

      {tiles.map((t) => {
        const def = widgetDef(t.widget);
        const shown = drag?.tileId === t.id ? drag.cand : t;
        const invalid = drag?.tileId === t.id && !drag.valid;
        return (
          <div
            key={t.id}
            onPointerDown={(e) => startMove(e, t)}
            className={cn(
              "absolute rounded-xl border bg-surface-muted/60 shadow-sm",
              editing && "animate-deck-jiggle cursor-grab",
              invalid ? "border-live ring-2 ring-live/50" : "border-border",
              drag?.tileId === t.id && "z-10 opacity-90",
            )}
            style={{
              left: `${(shown.x / DECK_GRID_COLS) * 100}%`,
              top: `${(shown.y / DECK_GRID_ROWS) * 100}%`,
              width: `${(shown.w / DECK_GRID_COLS) * 100}%`,
              height: `${(shown.h / DECK_GRID_ROWS) * 100}%`,
            }}
          >
            {/* In live mode the widget itself; while editing the widget "freezes" */}
            <div className={cn("h-full w-full overflow-hidden", editing && "pointer-events-none")}>
              {def ? (
                <def.Render
                  slug={slug}
                  state={state}
                  w={shown.w}
                  h={shown.h}
                  services={services}
                  widgetId={t.widget}
                />
              ) : (
                <div className="grid h-full place-items-center p-2 text-center text-[11px] text-ink-faint">
                  Noma'lum plitka: {t.widget}
                </div>
              )}
            </div>
            {editing && (
              <>
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => onRemove(t.id)}
                  className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full border border-border bg-surface text-ink-muted shadow"
                  aria-label="Plitkani olib tashlash"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <div
                  onPointerDown={(e) => startResize(e, t)}
                  className="absolute bottom-0 right-0 h-5 w-5 cursor-nwse-resize rounded-tl-md border-l border-t border-border bg-surface-elevated"
                  aria-label="O'lchamini o'zgartirish"
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
